import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {Card, CardContent} from "../../components/ui/card";
import NavigationButton from "../../components/ui/NavigationButton";
import DayStaffDistribution from "../../components/forms/staffDistributionForm/DayStaffDistribution";
import BackButton from "../../components/ui/BackNavigationButton";
import Tooltip from "../../components/ui/ToolTip";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import { pages } from "../../components/utils/pages-links";
import TutorialOverlay from "../../components/ui/TutorialOverlay";
import { useAlert } from "../../context/alert/AlertProvider";
import { useTranslation } from 'react-i18next';
import { Copy, Check } from 'lucide-react';
import { usePageTooltips } from "../../hooks/usePageTooltips";
import ScenarioIndicator from "../../components/forms/scenario/ScenarioIndicator";
import useAutoSave from "../../hooks/useAutoSave";

const StaffDistributionPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    
    const dayKeys = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday", "Holidays", "Eve of Holidays"];
    
    const tooltips = usePageTooltips();

    
    const [selectedDay, setSelectedDay] = useState('Monday');
    const [distributions, setDistributions] = useState({
        Monday: [],
        Tuesday: [],
        Wednesday: [],
        Thursday: [],
        Friday: [],
        Saturday: [],
        Sunday: [],
        Holidays: [],
        "Eve of Holidays": [],
    });
    const {showAlert} = useAlert();

    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialStep, setTutorialStep] = useState(0);
    const [copiedIntervals, setCopiedIntervals] = useState(null);
    const [copySuccess, setCopySuccess] = useState(null);

    const isExampleMode = location.state?.isExample;

    const daySelectRef = useRef(null);
    const intervalRef = useRef(null);
    const nextButtonRef = useRef(null);

    useAutoSave('distributions', distributions)

    const tutorialSteps = [
        {
            title: t("tutorial.staffDistribution.title"),
            description: t("tutorial.staffDistribution.description"),
            position:"center",
        },
        {
            title: t("tutorial.staffDistribution.day.title"),
            description: t("tutorial.staffDistribution.day.description"),
            position:"top-right",
            elementRef: daySelectRef,
        },
        {
            title: t("tutorial.staffDistribution.intervals.title"),
            description: t("tutorial.staffDistribution.intervals.description"),
            position:"top-right",
            elementRef: intervalRef,
        },
        {
            title: t("tutorial.staffDistribution.next.title"),
            description: t("tutorial.staffDistribution.next.description"),
            position:"next-button",
            elementRef:nextButtonRef
        }
    ];

    const getTranslatedDay = (dayKey) => {
        switch(dayKey) {
            case 'Monday': return t('days.monday');
            case 'Tuesday': return t('days.tuesday');
            case 'Wednesday': return t('days.wednesday');
            case 'Thursday': return t('days.thursday');
            case 'Friday': return t('days.friday');
            case 'Saturday': return t('days.saturday');
            case 'Sunday': return t('days.sunday');
            case 'Holidays': return t('days.holidays');
            case 'Eve of Holidays': return t('days.eveHolidays');
            default: return dayKey;
        }
    };

    useEffect(() => {
        if(isExampleMode) {
            setShowTutorial(true);
        }

        const savedDistributions = JSON.parse(sessionStorage.getItem('distributions'));
        if (savedDistributions) {
            setDistributions(savedDistributions);
        }
    }, [isExampleMode]);

    const handleCopy = (day) => {
        setCopiedIntervals(distributions[day]);
        setCopySuccess(day);
        
        setTimeout(() => {
            setCopySuccess(null);
        }, 2000);
    };

    const handlePaste = (targetDay) => {
        if (!copiedIntervals) return;

        setDistributions(prev => ({
            ...prev,
            [targetDay]: [...copiedIntervals]
        }));

        showAlert({
            title: t('common.success'),
            description: t('form.staffDistribution.intervalsCopied', { day: getTranslatedDay(targetDay) }),
            variant: 'success'
        });
    };

    const handleAddInterval = (day) => {
        setDistributions(prev => ({
            ...prev,
            [day]: [...prev[day], {
                startDay: t('form.schedule.today'),
                endDay: t('form.schedule.today'),
                startTime: '',
                endTime: '',
                staff: ''
            }]
        }));
    };

    const handleRemoveInterval = (day, index) => {
        setDistributions(prev => ({
            ...prev,
            [day]: prev[day].filter((_, i) => i !== index)
        }));
    };

    const handleIntervalChange = (day, index, field, value) => {
        setDistributions(prev => ({
            ...prev,
            [day]: prev[day].map((interval, i) => 
                i === index ? {...interval, [field]: value}: interval
            )
        }));
    };

    const handleNext = () => {
        const isValid = Object.values(distributions).every(dayDistributions =>
            dayDistributions.every(interval =>
              (interval.startTime !== undefined && 
               interval.startTime !== null && 
               (interval.startTime !== '' || interval.startTime === '00:00')) &&
              (interval.endTime !== undefined && 
               interval.endTime !== null && 
               (interval.endTime !== '' || interval.endTime === '00:00')) &&
              interval.staff
            )
          );
    
        if(!isValid) {
            showAlert({
                title: t('alert.error'),
                description: t('form.staffDistribution.fillAllFields'),
                variant:'destructive'
            });
            return;
        }

        const savedDistributions = sessionStorage.getItem('distributions');
        console.log(savedDistributions);
    
        navigate('/form/sseq', {
            state: {isExample: isExampleMode}
        });
    };

    const handleNextTutorialStep = () => {
        if (tutorialStep < tutorialSteps.length - 1) {
            setTutorialStep(prev => prev + 1);
        } else {
            setShowTutorial(false);
        }
    };

    const handleBack = () => {
        navigate('/form/staff', {
            state: { isExample: isExampleMode }
        });
    };

    return (
        <div className="-mt-16">
            <Breadcrumbs pages={pages}/>
            <div className="p-6 bg-gray-100 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="flex justify-between items-center mb-4">
                        <BackButton onClick={handleBack} />
                        <NavigationButton ref={nextButtonRef} onClick={handleNext} />
                    </div>

                    <ScenarioIndicator />
                    
                    <Card className="mt-5">
                        <CardContent className="p-4 sm:p-6">
                            <div className="flex flex-col items-center">
                                <div ref={daySelectRef}>
                                    <div className="flex items-center gap-2 mb-6">
                                        <h2 className="text-xl sm:text-2xl font-bold mb-2">
                                            {t("form.staffDistribution.title")}
                                        </h2>
                                        <div className="flex items-center h-8">
                                            <Tooltip content={tooltips.staffDistribution}/>
                                        </div>
                                    </div>
                                    
                                    <div className="block md:hidden w-full mb-4">
                                        <select 
                                            className="w-full p-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            value={selectedDay}
                                            onChange={(e) => setSelectedDay(e.target.value)}
                                        >
                                            {dayKeys.map(day => (
                                                <option key={day} value={day}>
                                                    {getTranslatedDay(day)}
                                                </option>
                                            ))}
                                        </select>
                                        {copiedIntervals && (
                                            <button
                                                onClick={() => handlePaste(selectedDay)}
                                                className="mt-2 w-full py-2 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50"
                                                title={t('form.staffDistribution.paste')}
                                            >
                                                {t('form.staffDistribution.paste')} {getTranslatedDay(selectedDay)}
                                            </button>
                                        )}
                                    </div>

                                    <div className="hidden md:flex md:flex-wrap gap-2 sm:gap-4 mb-6 overflow-x-auto pb-2">
                                        {dayKeys.map(day => (
                                            <div key={day} className="flex flex-col items-center gap-2">
                                                <button
                                                    onClick={() => setSelectedDay(day)}
                                                    className={`px-4 py-2 rounded-md whitespace-nowrap ${
                                                        selectedDay === day
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {getTranslatedDay(day)}
                                                </button>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleCopy(day)}
                                                        className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                                                        title={t('form.staffDistribution.copy')}
                                                        disabled={distributions[day].length === 0}
                                                    >
                                                        {copySuccess === day ? (
                                                            <Check className="w-4 h-4 text-green-500" />
                                                        ) : (
                                                            <Copy className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                    {copiedIntervals && (
                                                        <button
                                                            onClick={() => handlePaste(day)}
                                                            className="text-xs text-blue-600 hover:text-blue-700"
                                                            title={t('form.staffDistribution.paste')}
                                                        >
                                                            {t('form.staffDistribution.paste')}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <DayStaffDistribution
                                    key={selectedDay}
                                    ref={intervalRef}
                                    intervals={distributions[selectedDay]}
                                    onAddInterval={() => handleAddInterval(selectedDay)}
                                    onRemoveInterval={(index) => handleRemoveInterval(selectedDay, index)}
                                    onIntervalChange={(index, field, value) => 
                                        handleIntervalChange(selectedDay, index, field, value)
                                    }
                                    isDisabled={isExampleMode}
                                    translations={t}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
            <TutorialOverlay 
                isVisible={showTutorial}
                onClose={() => setShowTutorial(false)}
                title={tutorialSteps[tutorialStep]?.title}
                description={tutorialSteps[tutorialStep]?.description}
                position={tutorialSteps[tutorialStep]?.position}
                targetRef={tutorialSteps[tutorialStep]?.elementRef}
                onNext={handleNextTutorialStep}
                isLastStep={tutorialStep === tutorialSteps.length - 1}
            />
        </div>
    );
};

export default StaffDistributionPage;